import { usePath } from '@hooks/usePath'
import { getAccessDays } from '@utils/accessDays'
import { useMemo } from 'react'
import { Circle, Text, YStack, XStack } from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { formatDateShort } from '@utils/dateFormatter'
import { PathContinueSection } from './PathContinueSection'
import { PathProductsSection } from './PathProductSection'
import { PathGhostLoading } from './PathGhostLoading'
import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { useTranslation } from 'react-i18next'

export function PathContent() {
  const { enrollment, coursesInPath, enrollmentIsLoading, onLoadMore } =
    usePath()
  const { t } = useTranslation('CourseDetails')
  const pathHasCourses = coursesInPath.length > 0

  const accessDays = useMemo(() => {
    if (!enrollment) return null
    return getAccessDays({
      expireDate: enrollment.schoolProduct.expireDate,
      activatedAt: enrollment.activatedAt,
      availableUntil: enrollment.availableUntil,
      availableTimeLength: enrollment.schoolProduct.availableTimeLength,
    })
  }, [enrollment])

  const badgeInfo = useMemo(() => {
    return {
      badgeStyle:
        accessDays && accessDays.days <= 7 ? '$danger-300' : '$info-300',
      accessDaysLabel:
        accessDays && accessDays.days <= 7
          ? 'Atenção! acesso acaba em:'
          : 'Acesso liberado até:',
    }
  }, [accessDays])

  if (enrollmentIsLoading) return <PathGhostLoading />

  if (!enrollment) return null
  return (
    <YStack marginTop={50} flex={1} backgroundColor="$background-100">
      {pathHasCourses ? (
        <>
          <YStack
            paddingHorizontal="$spacing-inset-xs"
            paddingVertical="$spacing-inset-sm"
            backgroundColor="$neutral-0"
            testID="path-content"
          >
            {accessDays && (
              <XStack
                borderRadius="$border-radius-pill"
                padding="$spacing-inset-nano"
                marginBottom="$spacing-inset-xxs"
                bg="$neutral-100"
                alignItems="center"
                alignSelf="flex-start"
                testID="access-days-badge"
              >
                <Circle
                  bg={badgeInfo?.badgeStyle}
                  size="$spacing-md"
                  marginRight={8}
                >
                  <HeroIcon
                    icon={['fal', 'calendar-alt']}
                    size={16}
                    color="$neutral-0"
                  />
                </Circle>
                <Text
                  color="$neutral-800"
                  ff="$mulishBold"
                  fontSize="$font-size-xs"
                  marginRight="$spacing-inset-quarck"
                  testID="access-days-label"
                >
                  {badgeInfo?.accessDaysLabel}
                </Text>
                <Text
                  color={badgeInfo?.badgeStyle}
                  ff="$mulishBold"
                  fontSize="$font-size-xs"
                >
                  {formatDateShort(accessDays.expireDate)}
                </Text>
              </XStack>
            )}
            <Text
              color="$neutral-800"
              ff="$mulishBold"
              fontSize="$font-size-md"
              fontWeight={800}
              testID="product-title"
            >
              {enrollment?.schoolProduct.title}
            </Text>
          </YStack>
          <YStack
            paddingLeft="$spacing-inset-xs"
            paddingTop="$spacing-inset-md"
            backgroundColor="$background-100"
            justifyContent="center"
            gap="$spacing-inset-lg"
            flex={1}
          >
            <PathContinueSection />
            <PathProductsSection
              enrollmentId={enrollment.id.toString()}
              products={coursesInPath}
              enrollment={enrollment}
              onLoadMore={onLoadMore}
            />
          </YStack>
        </>
      ) : (
        <YStack
          paddingHorizontal="$spacing-inset-xs"
          paddingBottom="$spacing-inset-xxl"
          backgroundColor="$neutral-0"
          testID="empty-state"
        >
          <EmptyStateComponent
            title={t('emptyState.title')}
            description={t('emptyState.description')}
          />
        </YStack>
      )}
    </YStack>
  )
}
